import * as validation from "./validation";
import { logError } from "./debug";
import { now } from "./dates";
import { normalize } from "./string";

type ValueType = number | string | null | undefined;

export type SortFunction = (obj1: ValueType, obj2: ValueType) => number;

function auxEmptyCheck(a: boolean, b: boolean): number {
  if (!a && !b) {
    return -2;
  }
  if (a) {
    return b ? 0 : 1;
  }
  return b ? -1 : 0;
}

function sortNumbers(a: ValueType, b: ValueType): number {
  try {
    const anum = Number(a);
    const bnum = Number(b);
    const echeck = auxEmptyCheck(isNaN(anum), isNaN(bnum));
    if (echeck !== -2) {
      if (echeck === 0) {
        // Both values are NaN, we sort the non-numbers as well
        return sortDefault(a, b);
      }
      return echeck;
    }
    return anum - bnum;
  } catch (e) {
    logError("Number Sort Error", e);
    return 0;
  }
}

function sortDefault(a: ValueType, b: ValueType): number {
  try {
    const echeck = auxEmptyCheck(validation.isNull(a), validation.isNull(b));
    if (echeck !== -2) {
      return echeck;
    }
    if (!a || !b) return 0;
    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  } catch (e) {
    logError("Default Sort Error", e);
    return 0;
  }
}

export const sort = {
  numbers: (a: ValueType, b: ValueType): number => {
    return sortNumbers(a, b);
  },
  default: (a: ValueType, b: ValueType): number => {
    return sortDefault(a, b);
  },
  named(sortName: "numbers" | "default"): SortFunction {
    if (sortName === "numbers") {
      return this.numbers;
    } else if (sortName !== "default") {
      logError("Sorter doesn't exist:", sortName);
    }
    return this.default;
  }
};

export class Set {
  data: { [key: string]: boolean } = {};
  size = 0;

  add(item?: string) {
    if (!item) return;
    this.data[item] = true;
    this.size = Object.keys(this.data).length;
  }

  assignAll(arr: string[]) {
    this.data = {};
    arr.forEach((s) => this.add(s));
  }

  contains(item?: string): boolean {
    if (!item) {
      return false;
    }
    return !!this.data[item];
  }

  toArray(): string[] {
    const result = [];
    for (const k in this.data) {
      if (this.data[k]) result.push(k);
    }
    return result;
  }
}

export function makeSet(arr: string[]): Set {
  const result = new Set();
  result.assignAll(arr);
  return result;
}

export function sleepFor(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export type SleepStepFunction = (remaining: number, curStep: number) => void;

export async function sleepWithStep(totalMs: number, stepMs: number, stepFn: SleepStepFunction) {
  let remaining = totalMs;
  while (remaining > 0) {
    const step = remaining > stepMs ? stepMs : remaining;
    remaining -= step;
    await sleepFor(step);
    stepFn(remaining, step);
  }
}

export function copyToClipboard(textToCopy: string, input?: HTMLInputElement): boolean {
  let success = false;
  const textArea = input ? input : document.createElement("textarea");
  try {
    if (!input) {
      textArea.value = textToCopy;
      document.body.appendChild(textArea);
    }
    textArea.select();
    try {
      success = document.execCommand("copy");
    } catch (err) {
      logError("Copy error", err);
    }
  } catch (err) {
    logError("Error copying to clipboard", err);
  }
  if (!input) {
    document.body.removeChild(textArea);
  }
  return success;
}

export function scrollToTop(): boolean {
  try {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth"
    });
    return true;
  } catch (err) {
    logError("Failed scrollToTop", err);
  }
  return false;
}

export function pseudoUUID(): string {
  return now().format("YYYYMMDDHHmmss") + "A" + Math.round(Math.random() * 1000000);
}

export function searchTermFilter<T>(list: T[], searchTerm: string, fnGetProp: (item: T) => string | string[]): T[] {
  if (validation.isEmpty(searchTerm)) return list;
  const term = normalize(searchTerm);
  return list.filter((c) => {
    if (!term) return true;
    const terms = fnGetProp(c);
    if (typeof terms === "string") {
      return normalize(terms).includes(term);
    } else if (Array.isArray(terms)) {
      return terms.some((s) => normalize(s).includes(term));
    }
    return false;
  });
}
